import {
    GEOPORTAL_BASE_URL,
    MAPVIEW_SCALE,
    RCN_BASE_URL,
    WOJ_SLUGS,
} from '../core/constants';
import { esc, safeHref } from './formatters';
import { toAsciiSlug } from './string';

export function qstr(params) {
    var url = new URLSearchParams();

    Object.keys(params).forEach(function(key) {
        var value = params[key];
        if (value != null && value !== '') {
            url.set(key, value);
        }
    });

    return url.toString().replace(/%2C/gi, ',');
}

export function buildMapview(lat, lng) {
    return lat.toFixed(6) + '%2C' + lng.toFixed(6) + '%2C' + MAPVIEW_SCALE;
}

export function geoportalParcelUrl(id) {
    return GEOPORTAL_BASE_URL + '?identifyParcel=' + encodeURIComponent(id);
}

export function rcnParcelUrl(id) {
    return RCN_BASE_URL + '&identifyParcel=' + encodeURIComponent(id);
}

export function rcnMapviewUrl(lat, lng) {
    return RCN_BASE_URL + '&mapview=' + buildMapview(lat, lng);
}

export function geoportalMapviewUrl(lat, lng) {
    return 'https://mapy.geoportal.gov.pl/imapnext/imap/index.html?mapview=' + buildMapview(lat, lng);
}

export function googleMapsUrl(lat, lng) {
    return 'https://www.google.com/maps/@' + lat.toFixed(6) + ',' + lng.toFixed(6) + ',18z';
}

// One line under each service name in the "other services" card.
export const EXTERNAL_LINK_NOTES = {
    priceRegister: 'Oficjalne ceny z aktów notarialnych',
    geoportal: 'Granice działek, budynki i zdjęcia lotnicze',
    googleMaps: 'Zdjęcia satelitarne, widok ulicy i dojazd',
};

const EXTERNAL_LINK_ICON = '<svg width="16" height="16" aria-hidden="true" focusable="false"><use href="#icon-ext"/></svg>';
const NEW_TAB_NOTE = '<span class="sr-only"> (otwiera się w nowej karcie)</span>';

/**
 * "Zobacz to miejsce w innych serwisach": a detail card with one bordered tile
 * per service (shared .ms-related--tiles), each with the service name, an
 * optional one-line description and the external-link icon.
 */
export function renderExternalLinks(links) {
    var items = (links || []).map(function(link) {
        var href = safeHref(link && link.href);
        if (!href) return '';
        return '<li><a class="ms-related__link" href="' + esc(href) + '" target="_blank" rel="noopener noreferrer">'
            + '<span class="ms-related__name">' + esc(link.label) + '</span>'
            + (link.description ? ' <span class="ms-related__sub">' + esc(link.description) + '</span>' : '')
            + '<span class="ms-related__value">' + EXTERNAL_LINK_ICON + '</span>'
            + NEW_TAB_NOTE + '</a></li>';
    }).join('');
    if (!items) return '';
    return '<nav class="ms-card ms-card--grid nm-detail-block nm-place-links" aria-label="Zobacz to miejsce w innych serwisach">'
        + '<p class="nm-section-label">Zobacz to miejsce w innych serwisach</p>'
        + '<ul class="ms-related ms-related--tiles">' + items + '</ul>'
        + '</nav>';
}

/** Compact inline links to the official sources of a map layer (layer information boxes). */
export function renderSourceLinks(links) {
    var items = (links || []).map(function(link) {
        var href = safeHref(link && link.href);
        if (!href) return '';
        return '<a class="ms-link nm-ext-link" href="' + esc(href) + '" target="_blank" rel="noopener noreferrer">'
            + esc(link.label) + NEW_TAB_NOTE + ' <span aria-hidden="true">↗</span></a>';
    }).join('');
    return items ? '<nav class="nm-external-links" aria-label="Oficjalne źródła danych">' + items + '</nav>' : '';
}

export function normalizePublicIdentifier(value) {
    if (value == null) return null;

    var normalized = String(value).trim();
    if (!normalized || ['null', 'undefined', 'nan'].indexOf(normalized.toLowerCase()) !== -1) {
        return null;
    }

    return normalized;
}

function preferredDetailUrl(props) {
    return props ? safeHref(props.detail_url) : null;
}

export function buildDetailUrl(props) {
    var backendUrl = preferredDetailUrl(props);
    if (backendUrl) return backendUrl;

    var transactionId = normalizePublicIdentifier(props && props.tran_lokalny_id_iip);
    if (!transactionId) return null;

    var segments = [];
    var woj = props.teryt && props.teryt.length >= 2 ? props.teryt.substring(0, 2) : null;

    if (woj && WOJ_SLUGS[woj]) segments.push(WOJ_SLUGS[woj]);
    if (props.powiat_nazwa) segments.push(toAsciiSlug(props.powiat_nazwa));
    if (props.adres) {
        var segment = String(props.adres).split('|')[0];
        segment.split(';').forEach(function(pair) {
            var parts = pair.split(':');
            if (parts[0] && parts[1] && parts[0].trim() === 'MSC') {
                var city = toAsciiSlug(parts[1].trim());
                if (city) segments.push(city);
            }
        });
    }
    if (!segments.length) segments.push('transakcja');

    return '/transakcja/' + segments.join('/') + '/' + encodeURIComponent(transactionId);
}

export function buildParcelDetailUrl(props) {
    var backendUrl = preferredDetailUrl(props);
    if (backendUrl) return backendUrl;

    var slug = normalizePublicIdentifier(props && props.slug);
    if (!slug) return null;

    var segments = [];
    var woj = props.teryt && props.teryt.length >= 2 ? props.teryt.substring(0, 2) : null;

    if (woj && WOJ_SLUGS[woj]) segments.push(WOJ_SLUGS[woj]);
    if (props.numer) {
        segments.push(toAsciiSlug(props.numer));
    } else if (props.id_dzialki) {
        segments.push(toAsciiSlug(props.id_dzialki));
    }

    return '/dzialka/' + segments.join('/') + '/' + encodeURIComponent(slug);
}

export function buildBuildingDetailUrl(props) {
    var backendUrl = preferredDetailUrl(props);
    if (backendUrl) return backendUrl;

    var slug = normalizePublicIdentifier(props && props.slug);
    if (!slug) return null;

    var segments = [];
    var woj = props.teryt && props.teryt.length >= 2 ? props.teryt.substring(0, 2) : null;

    if (woj && WOJ_SLUGS[woj]) segments.push(WOJ_SLUGS[woj]);
    if (props.id_budynku) segments.push(toAsciiSlug(props.id_budynku));

    return '/budynek/' + segments.join('/') + '/' + encodeURIComponent(slug);
}

export function buildGunbDetailUrl(props) {
    var backendUrl = preferredDetailUrl(props);
    if (backendUrl) return backendUrl;

    var slug = normalizePublicIdentifier(props && props.slug);
    if (!slug) return null;

    var segments = [];
    var woj = props.teryt_wojewodztwo || (props.teryt && props.teryt.length >= 2 ? props.teryt.substring(0, 2) : null);

    if (woj && WOJ_SLUGS[woj]) segments.push(WOJ_SLUGS[woj]);
    if (props.miasto) segments.push(toAsciiSlug(props.miasto));
    if (props.rodzaj_inwestycji) segments.push(toAsciiSlug(props.rodzaj_inwestycji));

    return '/pozwolenie/' + segments.join('/') + '/' + encodeURIComponent(slug);
}
/* Symulacja nasłonecznienia 3D — deep link z opcjonalnym punktem kliknięcia,
   od którego startuje obserwator (patrz SunSimulationController). */
export function buildSunSimulationUrl(entityType, props, latLng) {
    var slug = normalizePublicIdentifier(props && props.slug);
    if (!slug) return null;

    var url = '/symulacja-naslonecznienia/' + entityType + '/' + encodeURIComponent(slug);
    if (latLng && isFinite(latLng.lat) && isFinite(latLng.lng)) {
        url += '?punkt=' + latLng.lat.toFixed(6) + ',' + latLng.lng.toFixed(6);
    }
    return url;
}

export function addSunSimulationStartPoint(url, latLng) {
    if (!url || !latLng || !isFinite(latLng.lat) || !isFinite(latLng.lng)) return url || null;

    return url + (url.indexOf('?') === -1 ? '?' : '&')
        + 'punkt=' + latLng.lat.toFixed(6) + ',' + latLng.lng.toFixed(6);
}
